import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * QuickCapture — a small floating composer pinned to the bottom of the screen.
 *
 * Type a thought, paste/drag an image, or paste a link, and Enter fires a
 * capture through the ingest pipeline (scoped to the active collection). The
 * block appears in the grid instantly via an optimistic insert keyed to a
 * client-generated id — the same id the pipeline persists under, so the real
 * block (and any comment) reconciles onto it when `/details` refetches.
 *
 * When a paste/drag (image or link) is accompanied by typed text, the block is
 * created from the attachment and the text rides along as a comment on it.
 */
import React, { useRef, useState } from "react";
import { ingestCapture, newClientBlockId } from "@/api/command-api";
import { addAnnotation } from "./annotations";
import "./quick-capture.scss";
/** A leading `http(s)://…` token, with any trailing text captured as the comment. */
const URL_RE = /^(https?:\/\/[^\s]+)(?:\s+([\s\S]+))?$/i;
const MAX_IMAGE_BYTES = 16 * 1024 * 1024;
/** Hostname of a URL (sans `www.`), for the link card's bottom-corner label. */
function hostOf(url) {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    }
    catch {
        return url;
    }
}
/**
 * A throwaway block envelope rendered in the grid the moment a capture fires,
 * before the pipeline returns. Cast through `unknown` because we only populate
 * the handful of fields the tile reads — the discriminated `content`/`metadata`
 * union is far wider than an optimistic placeholder needs.
 */
function optimisticEnvelope(id, collectionId, input) {
    const now = new Date().toISOString();
    const base = {
        id,
        collectionId,
        createdBy: "",
        title: "",
        summary: "",
        metadata: {},
        ingestorVersion: "",
        processingStatus: "processing",
        capturedAt: now,
        createdAt: now,
        surfaceAt: now,
    };
    if (input.kind === "image") {
        return {
            block: {
                ...base,
                type: "image",
                metadata: { thumbnail: { type: "external", url: input.dataUrl } },
                content: { file: { type: "external", url: input.dataUrl } },
            },
        };
    }
    if (input.kind === "link") {
        const host = hostOf(input.url);
        return {
            block: {
                ...base,
                type: "link",
                metadata: { source: { url: input.url, rawUrl: input.url, domain: host } },
                content: { subtype: "generic", url: input.url, generic: {} },
            },
        };
    }
    return {
        block: { ...base, type: "text", content: { text: input.text } },
    };
}
export const QuickCapture = ({ collectionId, onInsert, onRemove }) => {
    const [text, setText] = useState("");
    const [image, setImage] = useState(undefined);
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [notice, setNotice] = useState(undefined);
    const inputRef = useRef(null);
    const attachImage = (file) => {
        if (!file.type.startsWith("image/")) {
            setNotice("Only images can be attached.");
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setNotice("Image too large (max 16 MB).");
            return;
        }
        setNotice(undefined);
        const reader = new FileReader();
        reader.onload = () => setImage({ dataUrl: reader.result, file });
        reader.onerror = () => setNotice("Couldn’t read that image.");
        reader.readAsDataURL(file);
    };
    const onPaste = (e) => {
        const item = Array.from(e.clipboardData?.items ?? []).find((it) => it.kind === "file" && it.type.startsWith("image/"));
        if (!item)
            return; // not an image — let the text paste happen normally
        const file = item.getAsFile();
        if (!file)
            return;
        e.preventDefault();
        attachImage(file);
    };
    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = Array.from(e.dataTransfer?.files ?? []).find((f) => f.type.startsWith("image/"));
        if (file)
            attachImage(file);
    };
    const onDragOver = (e) => {
        if (!Array.from(e.dataTransfer?.types ?? []).includes("Files"))
            return;
        e.preventDefault();
        setDragOver(true);
    };
    const canSubmit = text.trim().length > 0 || image !== undefined;
    const submit = async () => {
        if (busy || !canSubmit)
            return;
        const trimmed = text.trim();
        const id = newClientBlockId();
        setBusy(true);
        setNotice(undefined);
        // Resolve what we're capturing + the comment that rides along (if any).
        let input;
        let content;
        let comment;
        let imageFile;
        if (image) {
            input = { kind: "image", dataUrl: image.dataUrl };
            imageFile = image.file;
            comment = trimmed || undefined;
        }
        else {
            const match = trimmed.match(URL_RE);
            if (match) {
                input = { kind: "link", url: match[1] };
                content = match[1];
                comment = (match[2] ?? "").trim() || undefined;
            }
            else {
                input = { kind: "text", text: trimmed };
                content = trimmed;
            }
        }
        // Optimistic: show the block (and any comment) in the active grid right away.
        if (collectionId) {
            onInsert?.(optimisticEnvelope(id, collectionId, input));
            if (comment)
                addAnnotation(id, { kind: "text", text: comment });
        }
        setText("");
        setImage(undefined);
        try {
            await ingestCapture({ clientBlockId: id, collectionId, content, image: imageFile });
            // No active collection to show it in — confirm inline instead.
            if (!collectionId) {
                setNotice("Captured");
                window.setTimeout(() => setNotice(undefined), 1500);
            }
        }
        catch (err) {
            if (collectionId)
                onRemove?.(id, collectionId);
            setNotice(err instanceof Error ? err.message : "Couldn’t capture.");
        }
        finally {
            setBusy(false);
        }
    };
    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void submit();
        }
    };
    return (_jsx("div", { className: "vld-capture", children: _jsxs("div", { className: `vld-capture__box${busy ? " is-busy" : ""}${dragOver ? " is-dragover" : ""}`, onDragOver: onDragOver, onDragLeave: () => setDragOver(false), onDrop: onDrop, children: [image ? (_jsxs("div", { className: "vld-capture__chip", children: [_jsx("img", { src: image.dataUrl, alt: "" }), _jsx("button", { type: "button", className: "vld-capture__chip-del", onClick: () => setImage(undefined), "aria-label": "Remove image", children: "\u00D7" })] })) : undefined, _jsx("textarea", { ref: inputRef, className: "vld-capture__input", value: text, rows: 1, placeholder: "Capture\u2026", "aria-label": "Quick capture", disabled: busy, onChange: (e) => setText(e.target.value), onPaste: onPaste, onKeyDown: onKeyDown }), notice ? _jsx("div", { className: "vld-capture__notice", children: notice }) : undefined] }) }));
};
